'use client';

import cx from 'classnames';
import { MouseEventHandler, ReactNode, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useClickAway } from 'react-use';

import { useResponsive } from '@flipster/react/misc/responsive/legacy';

import { PopupToDrawer } from '../PopupToDrawer';
import ChevronDownIcon from '../assets/svgs/chevron-down.svg';
import SuccessIcon from '../assets/svgs/success.svg';
import TriangleDownIcon from '../assets/svgs/triangle-down.svg';
import { SizeType } from '../types/base';
import styles from './selectBox.module.scss';

export interface SelectBoxItem {
  label: ReactNode;
  value: string | number;
  selectedLabel?: ReactNode;
}

export interface SelectBoxProps {
  size?: SizeType;
  shape?: 'default' | 'line' | 'none';
  listTheme?: 'dark' | 'line';
  listClassName?: string;
  title?: ReactNode;
  items: SelectBoxItem[];
  value?: string | number;
  onSelect?: (select: string | undefined) => void;
  placeholder?: ReactNode;
  disabled?: boolean;
  allLabel?: string;
  withHover?: boolean;
  withCheckedIcon?: boolean;
  withMaxHeight?: boolean;
  checkDisabled?: (value: string | number) => boolean;
  icon?: 'none' | 'down' | 'chevron';
  inner?: boolean;
  isFull?: boolean;
  className?: string;
  onOpen?: () => void;
}

export const SelectBox = ({
  size = 'large',
  shape = 'default',
  title,
  items,
  value,
  onSelect,
  placeholder,
  disabled,
  allLabel,
  withHover,
  withCheckedIcon,
  withMaxHeight = true,
  checkDisabled,
  icon = 'down',
  inner,
  isFull,
  listTheme = 'dark',
  listClassName,
  className,
  onOpen,
}: SelectBoxProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { isMobile } = useResponsive();

  const onClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    onSelect?.(e.currentTarget.value || undefined);
    setIsOpen(false);
  };

  const onClickOpen: MouseEventHandler<HTMLButtonElement> = () => {
    if (withHover) return;
    onOpen?.();
    setIsOpen((prevState) => !prevState);
  };

  const onMouseEnter = () => {
    if (!withHover) return;
    setIsOpen(true);
  };
  const onMouseLeave = () => {
    if (!withHover) return;
    setIsOpen(false);
  };

  const allItems = allLabel ? [{ label: allLabel, value: '' }, ...items] : items;

  const selectedItem = allItems.find((item) => String(item.value) === String(value ?? ''));

  const isDrawer = isMobile;

  useClickAway(dropdownRef, () => !withHover && !isDrawer && setIsOpen(false));

  return (
    <div
      className={cx(styles.wrapper, styles[size], className, {
        [styles.withHover]: withHover && isOpen,
        [styles.inner]: inner,
        [styles.isFull]: isFull,
        [styles.withMaxHeight]: withMaxHeight,
      })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={dropdownRef}
    >
      <button
        type='button'
        onClick={onClickOpen}
        className={cx(styles.select, styles[shape], { [styles.isOpen]: isOpen })}
        disabled={disabled}
        translate='no'
      >
        {(selectedItem?.selectedLabel || selectedItem?.label) ?? placeholder}
        {icon === 'down' && <TriangleDownIcon className={styles.icon} />}
        {icon === 'chevron' && <ChevronDownIcon className={styles.icon} />}
      </button>

      <PopupToDrawer
        theme={listTheme}
        title={title}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        popupClassName={listClassName}
        drawerClassName={cx(styles.drawerWrapper, listClassName)}
      >
        <ul className={cx(styles.list, styles[listTheme])}>
          {allItems.map((item) => (
            <li key={item.value}>
              <button
                type='button'
                value={item.value}
                onClick={onClick}
                className={cx(styles.item, { [styles.selected]: selectedItem?.value === item.value })}
                disabled={checkDisabled?.(item.value)}
                translate='no'
              >
                {item.label}
                {withCheckedIcon && isDrawer && selectedItem?.value === item.value && (
                  <SuccessIcon className={styles.checkIcon} />
                )}
              </button>
            </li>
          ))}
        </ul>
      </PopupToDrawer>
    </div>
  );
};

interface ControlProps extends Omit<SelectBoxProps, 'selected'> {
  name: string;
}

export const ControlSelectBox = ({ name, onSelect, ...restProps }: ControlProps) => {
  const { control } = useFormContext();
  const {
    field: { value, onChange },
  } = useController({ name, control });

  return (
    <SelectBox
      value={value}
      onSelect={(newValue) => {
        onChange(newValue);
        onSelect?.(newValue);
      }}
      {...restProps}
    />
  );
};
